/**
 * 튜토리얼 엔진 — 컨트롤러(진행) + 오버레이(렌더) + Port(데이터/저장/타겟)를 결선해 시나리오를 재생한다.
 *
 * 책임:
 * - 시나리오 시작(완료 시 스킵, 중단 지점 재개, 다시보기 강제),
 * - 진행(탭 onAdvance / 액션 notifyAction / 자동진행 타이머),
 * - 매 진행마다 재개 인덱스 저장 + 완료 시 완료 표기,
 * - 분석 이벤트.
 */
import Phaser from "phaser";
import { TutorialController } from "./TutorialController.js";
import { TutorialOverlay, type TutorialOverlayOptions } from "./TutorialOverlay.js";
import { isInteractionAllowed } from "./isInteractionAllowed.js";
import { applyRectOffset, type TutorialRect } from "./types.js";
import { resolveAutoAdvanceMs } from "./tutorialAutoAdvance.js";
import type {
  TutorialScenarioSource,
  TutorialTargetResolver,
  TutorialProgressStore,
  TutorialAnalyticsSink,
} from "./ports.js";

export interface TutorialRunnerOptions {
  readonly scene: Phaser.Scene;
  readonly source: TutorialScenarioSource;
  readonly resolver: TutorialTargetResolver;
  readonly store: TutorialProgressStore;
  /** 오버레이 설정 — onAdvance는 Runner가 주입하므로 제외. */
  readonly overlay: Omit<TutorialOverlayOptions, "onAdvance">;
  /**
   * autoAdvanceMs가 0인 메시지형(tap/spotlightTap) 스텝에 적용할 기본 자동 진행 시간.
   * action 스텝은 실제 액션 대기를 유지한다.
   */
  readonly defaultMessageAutoAdvanceMs?: number;
  readonly analytics?: TutorialAnalyticsSink;
}

export class TutorialRunner {
  private controller: TutorialController | null = null;
  private overlay: TutorialOverlay | null = null;
  private autoTimer: Phaser.Time.TimerEvent | null = null;
  private scenarioId = "";

  constructor(private readonly opts: TutorialRunnerOptions) {}

  /** 진행 중인가. */
  get isActive(): boolean {
    return this.controller !== null && this.controller.isActive;
  }

  /** 해당 시나리오가 정의돼 있는가(스텝 ≥1). 진입 트리거의 게이트용. */
  hasScenario(scenarioId: string): boolean {
    return this.opts.source.getScenario(scenarioId).length > 0;
  }

  get currentScenarioId(): string {
    return this.isActive ? this.scenarioId : "";
  }

  /** 현재 스텝 인덱스(비활성 -1). */
  get currentStepIndex(): number {
    return this.controller && this.controller.isActive ? this.controller.currentIndex : -1;
  }

  get currentStepId(): string {
    return this.controller?.currentStep?.stepId ?? "";
  }

  get currentAdvanceMode(): string {
    return this.controller?.currentStep?.advance ?? "";
  }

  /**
   * 게임 인터랙션 게이트 — 현재 스텝이 허용한 인터랙션인지. 호출자(게임 콜백 사이트)가 동작 직전 조회한다.
   * 비활성/미설정 스텝은 항상 true(무제한). 상세 정책은 `isInteractionAllowed`.
   */
  isInteractionAllowed(actionKey: string): boolean {
    return isInteractionAllowed(
      this.isActive,
      this.controller?.currentStep?.allowedActions ?? [],
      actionKey,
    );
  }

  /**
   * 시나리오 시작. 완료된 시나리오는 forceReplay가 아니면 스킵한다.
   * @returns 실제로 시작했으면 true.
   */
  startScenario(scenarioId: string, options: { forceReplay?: boolean } = {}): boolean {
    const forceReplay = options.forceReplay ?? false;
    if (!forceReplay && this.opts.store.isCompleted(scenarioId)) return false;

    const steps = this.opts.source.getScenario(scenarioId);
    if (steps.length === 0) return false;

    this.stop(); // 진행 중이던 오버레이/타이머 정리
    const startIndex = forceReplay ? 0 : this.opts.store.getResumeStep(scenarioId);
    this.scenarioId = scenarioId;
    this.controller = new TutorialController(steps, startIndex);
    this.overlay = new TutorialOverlay(this.opts.scene, {
      ...this.opts.overlay,
      onAdvance: () => this.advance(),
      onSkip: () => this.skip(),
    });

    this.opts.analytics?.log("tutorial_start", { scenario: scenarioId, step: this.controller.currentIndex });

    if (this.controller.isComplete) {
      this.finish();
      return true;
    }
    this.showCurrent();
    return true;
  }

  /** 다음 스텝으로(오버레이 탭/자동진행 경로). */
  advance(): void {
    if (!this.controller) return;
    this.controller.advance();
    this.afterMove();
  }

  /** 실제 액션 발생 통지 — 현재 스텝이 action 모드이고 키가 일치하면 진행. */
  notifyAction(actionKey: string): void {
    if (!this.controller) return;
    if (this.controller.notifyAction(actionKey)) this.afterMove();
  }

  /** 현재 시나리오를 스킵 완료 처리하고 오버레이를 닫는다. */
  skip(): void {
    if (!this.controller) return;
    this.opts.analytics?.log("tutorial_skip", {
      scenario: this.scenarioId,
      step: this.controller.currentIndex,
    });
    this.finish();
  }

  /** 진행 중단(완료 표기 없이 오버레이/타이머만 정리). */
  stop(): void {
    this.clearTimer();
    this.overlay?.destroy();
    this.overlay = null;
    this.controller = null;
  }

  private afterMove(): void {
    this.clearTimer();
    const c = this.controller;
    if (!c) return;
    this.opts.store.setResumeStep(this.scenarioId, c.currentIndex);
    if (c.isComplete) {
      this.finish();
      return;
    }
    this.showCurrent();
  }

  private showCurrent(): void {
    const c = this.controller;
    const overlay = this.overlay;
    if (!c || !overlay) return;
    const step = c.currentStep;
    if (!step) return;

    // 타겟 id → Rect 해석 + 데이터 offset 적용(패키지 레벨 보정).
    const resolveAdjusted = (targetId: string): TutorialRect | null => {
      const rect = this.opts.resolver.resolve(targetId);
      return rect ? applyRectOffset(rect, step.offsets[targetId]) : null;
    };

    const rects: TutorialRect[] = [];
    for (const targetId of step.targets) {
      const rect = resolveAdjusted(targetId);
      if (rect) rects.push(rect);
    }

    // 포인터(손가락) 연출 — from/to 타겟을 해석해 overlay에 전달.
    const gestureFrom = step.gesture !== "none" ? resolveAdjusted(step.gestureFrom) : null;
    const gestureTo = step.gesture === "drag" ? resolveAdjusted(step.gestureTo) : null;
    const autoAdvanceMs = resolveAutoAdvanceMs(step, this.opts.defaultMessageAutoAdvanceMs ?? 0);

    overlay.showStep(
      {
        messageKey: step.messageKey,
        advance: step.advance,
        autoAdvanceMs,
        position: step.position,
        padding: step.padding,
        unifyTargets: step.unifyTargets,
        gesture: step.gesture,
        gestureFrom,
        gestureTo,
      },
      rects,
    );

    this.opts.analytics?.log("tutorial_step", {
      scenario: this.scenarioId,
      index: c.currentIndex,
      stepId: step.stepId,
    });

    if (autoAdvanceMs > 0) {
      this.autoTimer = this.opts.scene.time.delayedCall(autoAdvanceMs, () => this.advance());
    }
  }

  private finish(): void {
    this.clearTimer();
    this.opts.store.markCompleted(this.scenarioId);
    this.opts.store.setResumeStep(this.scenarioId, 0);
    this.opts.analytics?.log("tutorial_complete", { scenario: this.scenarioId });
    this.overlay?.destroy();
    this.overlay = null;
    this.controller = null;
  }

  private clearTimer(): void {
    this.autoTimer?.remove(false);
    this.autoTimer = null;
  }
}
